import React, { useState } from "react";
import {
  Lock,
  User,
  ShieldCheck,
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
} from "lucide-react";

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // New State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("נא להזין שם משתמש וסיסמה");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://db2sg.com/tagweigh/api/auth?action=login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        }
      );

      const data = await response.json();

      if (response.ok && data.token) {
        localStorage.setItem("userToken", data.token);
        localStorage.setItem("userData", JSON.stringify(data.user));
        localStorage.setItem("role", data.user.role);
        localStorage.setItem("id", data.user.id?.toString());
        onLoginSuccess();
      } else {
        setError(data.message || "פרטי התחברות שגויים");
      }
    } catch (err) {
      setError("שגיאת תקשורת עם השרת");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F172A] flex items-center justify-center p-0 relative overflow-hidden font-sans"
      dir="ltr"
    >
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[120px]" />

      <div className="w-full max-w-[440px] z-10">
        {/* Brand Header */}
        <div className="text-center mb-0">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-slate-800 to-slate-900 rounded-[2rem] shadow-2xl border border-white/5 mb-6 group transition-transform hover:scale-105">
            <img
              src="./logo.jpg"
              className="w-12 h-12 object-contain"
              alt="Logo"
            />
          </div>
          <h1 className="text-3xl font-black text-white tracking-tighter mb-0">
            TAGWEIGH <span className="text-emerald-500 not-italic">V2.2</span>
          </h1>
        </div>

        {/* Login Card */}
        <div className="bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-10 shadow-2xl">
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm py-3 px-4 rounded-xl font-bold flex items-center gap-3 animate-shake">
                <ShieldCheck size={18} />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-[2px] mr-2">
                שם משתמש
              </label>
              <div className="relative group">
                <User
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-emerald-500 transition-colors"
                  size={20}
                />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="הזן שם משתמש"
                  className="w-full bg-slate-900/50 border border-green/5 rounded-2xl py-4 pr-12 pl-4 text-white font-bold outline-none focus:border-emerald-500/50 focus:ring-4 ring-emerald-500/10 transition-all placeholder:text-slate-600"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-[2px] mr-2">
                סיסמה
              </label>
              <div className="relative group">
                {/* Lock Icon (Right Side) */}
                <Lock
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-emerald-500 transition-colors"
                  size={20}
                />

                <input
                  type={showPassword ? "text" : "password"} // Dynamic Type
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  // Added pl-12 to make room for the eye icon on the left
                  className="w-full bg-slate-900/50 border border-green/5 rounded-2xl py-4 pr-12 pl-12 text-white font-bold outline-none focus:border-emerald-500/50 focus:ring-4 ring-emerald-500/10 transition-all placeholder:text-slate-600"
                />

                {/* Toggle Visibility Button (Left Side) */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-emerald-400 transition-colors focus:outline-none"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-800 text-slate-950 font-black py-4 rounded-2xl transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-3 group overflow-hidden relative"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span className="relative z-10 uppercase tracking-widest">
                    התחברות למערכת
                  </span>
                  <ArrowLeft
                    size={18}
                    className="relative z-10 group-hover:-translate-x-1 transition-transform"
                  />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
